import { USDA_TRANSLATION_LANGUAGE_IDS } from './useRecallsApi/constants';

import type { RecallsPill } from './constants';
import type {
  CpscRecallParams,
  FdaEnforcementParams,
  FdaEnforcementSearchParams,
  RecallsSearchParams,
  UsdaRecallParams,
  VehicleRecallsParams,
} from './interfaces';
import type { RecallsFormValues } from './types';

export function escapeFdaRecallNumberSearchFragment(query: string): string {
  return query.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function buildSearchParams(
  pill: RecallsPill,
  values: RecallsFormValues
): RecallsSearchParams {
  return {
    pill,
    displayTerm: buildDisplayTerm(pill, values),
    vehicleParams: buildVehicleParams(pill, values),
    fdaParams: buildFdaParams(pill, values),
    usdaParams: buildUsdaParams(pill, values),
    cpscParams: buildCpscParams(pill, values),
  };
}

export function hasActiveSearch(params: RecallsSearchParams | null): boolean {
  if (!params) {
    return false;
  }
  return (
    params.vehicleParams !== null ||
    params.fdaParams !== null ||
    params.usdaParams !== null ||
    params.cpscParams !== null
  );
}

// ---------------------------------------------------------------------------
// Vehicle
// ---------------------------------------------------------------------------

function cleanStr(s: string | undefined): string | undefined {
  const trimmed = (s ?? '').trim();
  return trimmed || undefined;
}

function buildVehicleParams(
  pill: RecallsPill,
  values: RecallsFormValues
): VehicleRecallsParams | null {
  if (pill !== 'Vehicle') {
    return null;
  }

  const searchType = values.searchType ?? 'VIN';
  const query = (values.query ?? '').trim();

  switch (searchType) {
    case 'VIN': {
      const vin = cleanStr(values.vin) || cleanStr(query);
      return vin ? { vin } : null;
    }
    case 'LICENSE_PLATE': {
      const plate = cleanStr(values.licensePlate);
      const state = (values.state ?? 'All').trim();
      return plate ? { license_plate: plate, ...(state && { state }) } : null;
    }
    default: {
      const year = cleanStr(values.year);
      const make = cleanStr(values.make);
      const model = cleanStr(values.model);
      const trim = cleanStr(values.trim);
      return year && make && model
        ? { year, make, model, ...(trim && { trim }) }
        : null;
    }
  }
}

// ---------------------------------------------------------------------------
// FDA
// ---------------------------------------------------------------------------

function buildFdaParams(
  pill: RecallsPill,
  values: RecallsFormValues
): FdaEnforcementParams | null {
  const domainMap: Record<RecallsPill, 'drug' | 'device' | 'food' | null> = {
    Vehicle: null,
    Food: 'food',
    Drugs: 'drug',
    'Medical Devices': 'device',
    'Consumer Products': null,
  };
  const domain = domainMap[pill];
  if (!domain) {
    return null;
  }

  const query = (values.query ?? '').trim();
  const searchType = values.searchType ?? '';

  let search: string | undefined;
  if (query) {
    if (searchType === 'recall_number') {
      search = `recall_number:"${escapeFdaRecallNumberSearchFragment(query)}"`;
    } else {
      search = query;
    }
  }

  const searchParams: FdaEnforcementSearchParams = {
    ...(search && { search }),
  };

  return { domain, searchParams };
}

// ---------------------------------------------------------------------------
// USDA
// ---------------------------------------------------------------------------

function buildUsdaParams(
  pill: RecallsPill,
  values: RecallsFormValues
): UsdaRecallParams | null {
  if (pill !== 'Food') {
    return null;
  }

  const baseParams: UsdaRecallParams = {
    field_translation_language: USDA_TRANSLATION_LANGUAGE_IDS.English,
  };

  const query = (values.query ?? '').trim();
  if (!query) {
    return baseParams;
  }

  const searchType = values.searchType ?? '';
  if (searchType === 'recall_number') {
    return { ...baseParams, field_recall_number: query };
  }
  return { ...baseParams, field_summary_value: query };
}

// ---------------------------------------------------------------------------
// CPSC
// ---------------------------------------------------------------------------

function buildCpscParams(
  pill: RecallsPill,
  values: RecallsFormValues
): CpscRecallParams | null {
  if (pill !== 'Consumer Products') {
    return null;
  }

  const query = (values.query ?? '').trim();
  if (!query) {
    return {};
  }

  const searchType = values.searchType ?? '';
  if (searchType === 'recall_number') {
    return { RecallNumber: query };
  }
  return { ProductName: query };
}

// ---------------------------------------------------------------------------
// Display term
// ---------------------------------------------------------------------------

function buildDisplayTerm(
  pill: RecallsPill,
  values: RecallsFormValues
): string {
  if (pill === 'Vehicle') {
    const searchType = values.searchType ?? 'VIN';
    if (searchType === 'VIN') {
      return (values.vin || values.query || '').trim() || '—';
    }
    if (searchType === 'LICENSE_PLATE') {
      const plate = (values.licensePlate ?? '').trim();
      const state = (values.state ?? 'All').trim();
      return state && state !== 'All' ? `${plate}, ${state}` : plate || '—';
    }
    const year = (values.year ?? '').trim();
    const make = (values.make ?? '').trim();
    const model = (values.model ?? '').trim();
    const trimVal = (values.trim ?? '').trim();
    return [year, make, model, trimVal].filter(Boolean).join(' ') || '—';
  }
  return (values.query ?? '').trim() || '—';
}
